import { action, observable, runInAction } from 'mobx';

import { ContextStore, SerializedData, Store } from 'bernie-core';
import { getBexApiContext } from 'bernie-graphql';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';

import { BexApiSource } from 'sources/bex-api-source';

import { mapFindAnswerReviews, mapReviews } from 'stores/adapters/bex-api/reviews/reviews-adapter';
import { mapQuestions } from 'stores/adapters/bex-api/questions-answers/questions-answers-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import { QuestionsViewModel, ReviewsViewModel } from 'models';

import {
  ContextInput,
  CreateQuestion,
  FindAnswersSearch,
  ProductQuestionTopic,
  PropertyReviewSort,
  QuestionAnswers,
  Reviews
} from '__generated__/api';

export enum FindAnswersFormLaunchLocation {
  INFOSITE = 'infosite',
  REVIEWS_DIALOG = 'reviewsDialog'
}

export const FIND_ANSWERS_TO_RETRIEVE = 10;
const FIND_ANSWERS_STARTING_INDEX = 0;
const QUESTION_DATE_FORMAT = "uuuu-MM-dd'T'HH:mm:ss'Z'";

interface FindAnswersStoreDefaults {
  reviews: ReviewsViewModel;
  questionAnswers: QuestionsViewModel;
}

export class FindAnswersStore extends Store {
  public static DEFAULTS: FindAnswersStoreDefaults = {
    reviews: {
      count: null,
      cleanliness: null,
      userReviews: [],
      superlative: null,
      amenityScore: null,
      serviceAndStaff: null,
      overallRating: null,
      hotelCondition: null,
      totalReviewsRequested: 0,
      fetchedEmptyReviews: null
    },
    questionAnswers: {
      count: null,
      userQuestions: [],
      totalQuestionAnswersRequested: 0
    }
  };

  @observable
  public formLaunchLocation: FindAnswersFormLaunchLocation;
  @observable
  public reviews: ReviewsViewModel = FindAnswersStore.DEFAULTS.reviews;
  @observable
  public createQuestionId: string;
  @observable
  public questionAnswers: QuestionsViewModel = FindAnswersStore.DEFAULTS.questionAnswers;

  constructor(private bexApiSource: BexApiSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    delete data.bexApiSource;

    Object.assign(this, data);
  }

  @action
  public setFormLaunchLocation(formLaunchLocation: FindAnswersFormLaunchLocation) {
    this.formLaunchLocation = formLaunchLocation;
  }

  @action
  public clearFindAnswersResult(): void {
    runInAction('clear find answers result', () => {
      this.reviews = FindAnswersStore.DEFAULTS.reviews;
      this.questionAnswers = FindAnswersStore.DEFAULTS.questionAnswers;
    });
  }

  @action
  public fetchFindAnswers(context: ContextStore, hotelId: number, searchString: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);

    this.clearFindAnswersResult();
    const variables: FindAnswersSearch.Variables = {
      context: contextInput,
      propertyId: hotelId.toString(),
      reviewSortBy: PropertyReviewSort.NEWEST_TO_OLDEST,
      reviewFilters: {
        includeRecentReviews: false,
        searchTerm: searchString
      },
      reviewPagination: {
        startingIndex: FIND_ANSWERS_STARTING_INDEX,
        size: FIND_ANSWERS_TO_RETRIEVE
      },
      productQuestionTopic: ProductQuestionTopic.HOTEL,
      questionPagination: {
        startingIndex: FIND_ANSWERS_STARTING_INDEX,
        size: FIND_ANSWERS_TO_RETRIEVE
      },
      questionFilters: {
        searchTerm: searchString
      },
      questionDateFormat: QUESTION_DATE_FORMAT
    };

    return this.bexApiSource
      .fetchFindAnswersSearch(variables)
      .then(({ data }) => {
        runInAction('update bex api find answers search from response', () => {
          if (data.propertyInfo && data.propertyInfo.reviewInfo) {
            this.reviews = mapFindAnswerReviews(this.reviews, data.propertyInfo.reviewInfo, FIND_ANSWERS_TO_RETRIEVE);
          }
          if (data.productQuestions && data.productQuestions.questions) {
            this.questionAnswers = mapQuestions(this.questionAnswers, data.productQuestions, FIND_ANSWERS_TO_RETRIEVE);
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api find answers search call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.BEX_API_FIND_ANSWERS_STORE_FETCH_ERROR, variables, getErrorString(error));
        });

        throw error;
      });
  }

  @action
  public fetchReviews(context: ContextStore, hotelId: number, searchString: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const reviews = this.reviews;

    const nextIndex = (reviews && reviews.userReviews && reviews.userReviews.length) || 0;
    const variables: Reviews.Variables = {
      context: contextInput,
      propertyId: hotelId.toString(),
      sortBy: PropertyReviewSort.NEWEST_TO_OLDEST,
      filters: {
        includeRecentReviews: false,
        searchTerm: searchString
      },
      pagination: {
        startingIndex: nextIndex,
        size: FIND_ANSWERS_TO_RETRIEVE
      }
    };

    return this.bexApiSource
      .fetchReviews(variables)
      .then(({ data }) => {
        runInAction('update bex api reviews search from response', () => {
          if (data.propertyInfo && data.propertyInfo.reviewInfo) {
            this.reviews = mapReviews(this.reviews, data.propertyInfo.reviewInfo, nextIndex + FIND_ANSWERS_TO_RETRIEVE);
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api reviews search call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.BEX_API_FIND_ANSWERS_STORE_FETCH_ERROR, variables, getErrorString(error));
        });

        throw error;
      });
  }

  @action
  public fetchQuestionAnswers(context: ContextStore, hotelId: number, searchString: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const questionAnswers = this.questionAnswers;

    const nextIndex = (questionAnswers && questionAnswers.userQuestions && questionAnswers.userQuestions.length) || 0;
    const variables: QuestionAnswers.Variables = {
      context: contextInput,
      pagination: {
        startingIndex: nextIndex,
        size: FIND_ANSWERS_TO_RETRIEVE
      },
      productId: hotelId.toString(),
      productQuestionTopic: ProductQuestionTopic.HOTEL,
      filters: {
        searchTerm: searchString
      },
      questionDateFormat: QUESTION_DATE_FORMAT
    };

    return this.bexApiSource
      .fetchQuestionAnswers(variables)
      .then(({ data }) => {
        runInAction('update bex api questionAnswers search from response', () => {
          if (data.productQuestions && data.productQuestions.questions) {
            this.questionAnswers = mapQuestions(this.questionAnswers, data.productQuestions, nextIndex + FIND_ANSWERS_TO_RETRIEVE);
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api questionAnswers search call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.BEX_API_FIND_ANSWERS_STORE_FETCH_ERROR, variables, getErrorString(error));
        });

        throw error;
      });
  }

  @action
  public createQuestion(context: ContextStore, hotelId: number, questionerEmail: string, questionText: string, questionerName: string) {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: CreateQuestion.Variables = {
      context: contextInput,
      productId: hotelId.toString(),
      productQuestionTopic: ProductQuestionTopic.HOTEL,
      email: questionerEmail,
      question: questionText,
      questioner: questionerName
    };

    return this.bexApiSource
      .createQuestion(variables)
      .then(({ data }) => {
        runInAction('update conversation from response', () => {
          this.createQuestionId = data.askProductQuestion;
        });
      })
      .catch(error => {
        runInAction('recovering from conversation create failure', () => {
          this.createQuestionId = null;
          this.logger.logEvent(STORE_EVENTS.BEX_API_CREATE_QUESTION_ERROR, variables, getErrorString(error));
        });
      });
  }
}
